import { mapToTags, tagMapsEqual, tagsToMap, type TagMap } from "@/components/DashboardEditFields";
import { type Dashboard } from "@/data/dashboards";
import {
  useArchiveDashboard,
  useDashboards,
  useDeleteDashboard,
  useSetDashboardTags,
  useUpdateDashboard,
} from "@/hooks/useDashboards";
import { useProperties } from "@/hooks/useProperties";
import { useTenant } from "@/hooks/useTenant";
import { computeEditChanges, sortDashboardsForAdmin } from "@/lib/settingsHelpers";
import { Plus } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";

import { DashboardDeleteDialog } from "./DashboardDeleteDialog";
import { DashboardListItem } from "./DashboardListItem";
import { DuplicateDashboardDialog, type DuplicateSource } from "./DuplicateDashboardDialog";
import { type DashboardRow } from "./types";

/** Only a SimplySpark row has a definition to copy; every other row gets no Duplicate action. */
function duplicateAction(
  d: Dashboard,
  open: (source: DuplicateSource) => void,
): (() => void) | undefined {
  const sparkDashboardId = d.sparkDashboardId;
  if (d.type !== "spark" || !sparkDashboardId) return undefined;
  return () => open({ sparkDashboardId, title: d.title });
}

// SIM-1443 — encapsulates the inline-edit row state and commit flow. Title /
// URL / year / category go through the existing dashboard mutation; tags go
// through a separate mutation that only fires when the user actually touched
// the Properties section.
function useDashboardEditState(dashboards: DashboardRow[]) {
  const updateDashboard = useUpdateDashboard();
  const setDashboardTags = useSetDashboardTags();

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState("");
  const [editUrl, setEditUrl] = useState("");
  const [editCategory, setEditCategory] = useState("");
  const [editSchoolYear, setEditSchoolYear] = useState("");
  const [editTagMap, setEditTagMap] = useState<TagMap>({});

  const startEdit = (d: DashboardRow) => {
    setEditingId(d.id);
    setEditTitle(d.title);
    setEditUrl(d.url);
    setEditCategory(d.category);
    setEditSchoolYear(d.school_year);
    setEditTagMap(tagsToMap(d.tags));
  };

  const commitEdit = () => {
    if (!editingId) return;
    const dash = dashboards.find((d) => d.id === editingId);
    if (dash) {
      const changes = computeEditChanges(
        {
          id: editingId,
          title: editTitle,
          url: editUrl,
          category: editCategory,
          school_year: editSchoolYear,
        },
        dash,
      );
      if (changes) updateDashboard.mutate(changes);
      const originalMap = tagsToMap(dash.tags);
      if (!tagMapsEqual(originalMap, editTagMap)) {
        setDashboardTags.mutate({ dashboardId: editingId, tags: mapToTags(editTagMap) });
      }
    }
    setEditingId(null);
  };

  return {
    editingId,
    editTitle,
    editUrl,
    editCategory,
    editSchoolYear,
    editTagMap,
    startEdit,
    commitEdit,
    cancelEdit: () => setEditingId(null),
    setEditTitle,
    setEditUrl,
    setEditCategory,
    setEditSchoolYear,
    setEditTagMap,
  };
}

export function DashboardManagement() {
  const { tenantPath } = useTenant();
  const { data: dashboards, isLoading } = useDashboards();
  const archiveDashboard = useArchiveDashboard();
  const deleteDashboard = useDeleteDashboard();
  // SIM-1464 — year/category option lists are derived from PropertyDef
  // (auto-seeded per tenant) inside <DashboardEditFields>. The legacy
  // `useSettingList` reads were removed in this slice; the PortalSetting
  // rows are dropped by the accompanying migration.
  const { data: properties } = useProperties();
  const edit = useDashboardEditState(dashboards);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [duplicateSource, setDuplicateSource] = useState<DuplicateSource | null>(null);

  if (isLoading) return <p className="text-sm text-muted-foreground">Loading dashboards...</p>;

  const sorted = sortDashboardsForAdmin(dashboards);

  return (
    <div className="max-w-3xl space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {dashboards.length} {dashboards.length === 1 ? "dashboard" : "dashboards"} in this tenant.
        </p>
        <Link
          to={tenantPath("/add-dashboard")}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded text-sm font-medium text-primary-foreground bg-primary hover:bg-primary/90 transition-colors"
        >
          <Plus className="h-4 w-4" /> Add Dashboard
        </Link>
      </div>
      {sorted.length === 0 ? (
        <p className="text-sm text-muted-foreground">No dashboards yet.</p>
      ) : (
        <ul className="border border-border rounded divide-y divide-border">
          {sorted.map((d) => (
            <li key={d.id} className={`px-4 py-3 text-sm ${d.archived ? "opacity-50" : ""}`}>
              <DashboardListItem
                d={d}
                editingId={edit.editingId}
                dashboardId={d.id}
                dashboardType={d.type}
                editTitle={edit.editTitle}
                editUrl={edit.editUrl}
                editSchoolYear={edit.editSchoolYear}
                editCategory={edit.editCategory}
                properties={properties}
                tagMap={edit.editTagMap}
                onStartEdit={edit.startEdit}
                onCommitEdit={edit.commitEdit}
                onCancelEdit={edit.cancelEdit}
                onSetEditTitle={edit.setEditTitle}
                onSetEditUrl={edit.setEditUrl}
                onSetEditSchoolYear={edit.setEditSchoolYear}
                onSetEditCategory={edit.setEditCategory}
                onSetTagMap={edit.setEditTagMap}
                onArchiveToggle={(id, archived) =>
                  archiveDashboard.mutate({ id, archived: !archived })
                }
                onDelete={setDeleteId}
                onDuplicate={duplicateAction(d, setDuplicateSource)}
              />
            </li>
          ))}
        </ul>
      )}
      <DashboardDeleteDialog
        dashboards={sorted}
        deleteId={deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={(id) => {
          deleteDashboard.mutate(id);
          setDeleteId(null);
        }}
      />
      <DuplicateDashboardDialog source={duplicateSource} onClose={() => setDuplicateSource(null)} />
    </div>
  );
}
